import React, { useState, useEffect, useRef } from 'react';
import classes from './MassSpectrometry.module.css';
import { useAtom } from 'jotai';
import { Row, Col, Typography, Button, DatePicker, Tooltip, Input } from 'antd';
import dayjs from 'dayjs';
import { userNameState, isAdminState, massSpectrometryTableDataState, massSpectrometryTableExpandedRowState, currentMassSpecIDState, massSpecTourOpenState } from '../../stateManagement/commonState';
import { PlusOutlined, RedoOutlined, LockOutlined } from '@ant-design/icons';
import MassSpectrometryTable from './MassSpectrometryTable';
import { addMassSpecRow, getUserPinCode } from '../../utils/dbHelpers';
import PipelineSummaryTable from './PipelineSummaryTable';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const defaultRange = {
    from: dayjs().subtract(8, 'weeks'),
    to: dayjs()
};

function MassSpectrometryContainer() {
    const [username] = useAtom(userNameState);
    const [isAdmin] = useAtom(isAdminState);
    const [massSpecDateRange, setMassSpecDateRange] = useState(defaultRange);
    const [massSpectrometryTableData, setMassSpectrometryTableData] = useAtom(massSpectrometryTableDataState);
    const [, setMassSpectrometryTableExpandedRow] = useAtom(massSpectrometryTableExpandedRowState);
    const [filteredInfo, setFilteredInfo] = useState({});
    const [massSpecTourOpen, setMassSpecTourOpen] = useAtom(massSpecTourOpenState);
    const [currentMassSpecID, setCurrentMassSpecID] = useAtom(currentMassSpecIDState);
    const newMassSpecRowRef = useRef(null);
    const [requiredPin, setRequiredPin] = useState(null);
    const [pinInput, setPinInput] = useState('');
    const [unlocked, setUnlocked] = useState(null); // null while checking
    const [pinError, setPinError] = useState(false);

    useEffect(() => {
        if (!username) return;
        getUserPinCode(username).then((pin) => {
            setRequiredPin(pin || null);
            setUnlocked(!pin);
        });
    }, [username]);

    const rangePickerChangeHandler = async (_, range) => {
        setMassSpecDateRange({ from: range[0], to: range[1] });
    }

    const addAnalysisRequestHandler = async () => {
        let res = await addMassSpecRow(username);
        setCurrentMassSpecID(res[0].id);
        newMassSpecRowRef.current = { key: res[0].id, ...res[0] };
        setFilteredInfo({});
    }

    useEffect(() => {
        if (currentMassSpecID) {
            setMassSpectrometryTableData([newMassSpecRowRef.current, ...massSpectrometryTableData]);
            setMassSpectrometryTableExpandedRow([newMassSpecRowRef.current?.key]);
            setMassSpecTourOpen(true);
            newMassSpecRowRef.current = null;
        }
    }, [currentMassSpecID])

    if (!unlocked) {
        if (unlocked === null) return null;
        const tryUnlock = () => {
            if (pinInput === requiredPin) setUnlocked(true);
            else {
                setPinError(true);
                setTimeout(() => setPinError(false), 2000);
            }
        };
        return (
            <>
                <Row style={{ marginTop: '12px' }}>
                    <Title level={3}>Laboratory Tests</Title>
                </Row>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 28px', borderRadius: 12, background: '#fafafa', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Input.Password
                                status={pinError ? 'error' : undefined}
                                placeholder="PIN"
                                value={pinInput}
                                onChange={(e) => { setPinInput(e.target.value); setPinError(false); }}
                                onPressEnter={tryUnlock}
                                style={{ width: 160 }}
                            />
                            <Button type="primary" onClick={tryUnlock}>Unlock</Button>
                        </div>
                        {pinError && <span style={{ color: '#ff4d4f', fontSize: 13 }}>Incorrect PIN</span>}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Row style={{ marginTop: '12px' }} justify="space-between" align="middle">
                <Title level={3} style={{ margin: 0 }}>Laboratory Tests</Title>
                {requiredPin && (
                    <Tooltip title="Lock" placement="topRight">
                        <Button
                            type="default"
                            icon={<LockOutlined />}
                            onClick={() => { setUnlocked(false); setPinInput(''); }}
                        />
                    </Tooltip>
                )}
            </Row>
            <Row>
                <Col span={24} style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={isAdmin ? "Add Analysis" : "Request Analysis"} placement="topLeft">
                        <Button
                            type="default"
                            icon={<PlusOutlined />}
                            onClick={() => { addAnalysisRequestHandler() }}
                        />
                    </Tooltip>
                    <RangePicker
                        style={{ flex: '1', margin: '0 3px' }}
                        className={classes.rangePicker}
                        onChange={rangePickerChangeHandler}
                        value={[dayjs(massSpecDateRange.from), dayjs(massSpecDateRange.to)]}
                        presets={[
                            { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                            { label: 'Last Week', value: [dayjs().subtract(1, 'weeks').startOf('week'), dayjs().subtract(1, 'weeks').endOf('week')] },
                            { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                            { label: 'Last 4 Weeks', value: [dayjs().subtract(3, 'weeks').startOf('week'), dayjs().endOf('week')] },
                            { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                            { label: 'This Year', value: [dayjs().startOf('year'), dayjs().endOf('year')] }
                        ]}
                        allowClear={false}
                        format={(value) => dayjs(value).format('MMM D, YYYY')}
                    />
                    <Tooltip title="Clear Filters" placement="topRight">
                        <Button
                            type="default"
                            icon={<RedoOutlined />}
                            onClick={() => { setFilteredInfo({}); }}
                        />
                    </Tooltip>
                </Col>
            </Row>
            <Row justify="center" align="middle">
                <Col span={24} style={{ textAlign: "center" }} >
                    <MassSpectrometryTable massSpecDateRange={massSpecDateRange} filteredInfo={filteredInfo} setFilteredInfo={setFilteredInfo} />
                </Col>
            </Row>
            {
                isAdmin ?
                    <>
                        <Row style={{ marginTop: '12px' }}>
                            <Title level={3}>Pipeline Summary</Title>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col span={24} style={{ textAlign: "center" }} >
                                <PipelineSummaryTable />
                            </Col>
                        </Row>
                    </> : null
            }
        </>
    )
}

export default MassSpectrometryContainer;
